import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';

import ModalPortal from '../../components/modal-portal';
import { SenderPrepay } from './constants';
import * as UserActions from '../../store/user/user.actions';
import * as UserAPI from '../../store/user/user.api';
import { getUserSettingPrepayDutyAlert, createUserSettingPrepayDutyAlertFromSetting } from './helpers';
import { alertModalMessages } from './messages';
import { AvailableDutiesOptionsProptype } from './prop-types';
import style from './style.module.less';

/**
 * @typedef {import('../../fulfillment-planner/duties-and-taxes/prop-types').AvailableDutiesOptions} AvailableDutiesOptions
 */

/**
 * @typedef {import('../../fulfillment-planner/duties-and-taxes/prop-types').AvailableDutyOption} AvailableDutyOption
 */

/**
 * @param {object} props
 * @param {AvailableDutiesOptions} props.availableDutiesOptions
 * @param {(senderPrepay: AvailableDutyOption, availableDutiesOptions: AvailableDutiesOptions) => void} props.onChange
 * @param {() => void} props.onClose
 * @param {() => void} props.onContinue
 * @returns {React.JSX.Element}
 */
const PrepayDutyAlertModal = ({ availableDutiesOptions, onChange, onClose, onContinue }) => {
  const dispatch = useDispatch();

  const existingSetting = useSelector(getUserSettingPrepayDutyAlert);

  const createAndDismissSetting = useMemo(() => {
    return createUserSettingPrepayDutyAlertFromSetting(existingSetting, {
      createdPrepayDutyOn: moment().toISOString(),
      dismissedOn: moment().toISOString(),
    });
  }, [existingSetting]);
  const dismissSetting = useMemo(() => {
    return createUserSettingPrepayDutyAlertFromSetting(existingSetting, {
      dismissedOn: moment().toISOString(),
    });
  }, [existingSetting]);

  const handleOnClose = useCallback(() => {
    onClose();
    onContinue();
  }, [onClose, onContinue]);

  const handleSaveUserSetting = useCallback(
    async (setting) => {
      try {
        await UserAPI.saveUserSettingPromise(setting);
        dispatch(UserActions.saveUserSettingSuccess(setting));
      } catch {
        dispatch(UserActions.updateUserSetting(setting));
      } finally {
        handleOnClose();
      }
    },
    [dispatch, handleOnClose],
  );

  const handleChangeDutiesAndTaxes = useCallback(() => {
    const senderPrepay = availableDutiesOptions.find(({ additionalOptionValue }) => additionalOptionValue.prepaidDuty === SenderPrepay);
    onChange(senderPrepay, availableDutiesOptions);
    handleSaveUserSetting(createAndDismissSetting);
  }, [availableDutiesOptions, createAndDismissSetting, handleSaveUserSetting, onChange]);

  const handleProceed = useCallback(() => {
    handleSaveUserSetting(dismissSetting);
  }, [dismissSetting, handleSaveUserSetting]);

  const handleModalPortalOnClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <ModalPortal onClose={handleModalPortalOnClose}>
      <Modal size='small'>
        <ModalHeader>
          <FormattedMessage {...alertModalMessages.title} />
        </ModalHeader>
        <ModalBody className={style['prepay-duty-alert-modal__modal-body']}>
          <FormattedMessage
            {...alertModalMessages.message}
            values={{
              p: (chunks) => <p>{chunks}</p>,
              strong: (chunks) => <strong>{chunks}</strong>,
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={handleChangeDutiesAndTaxes}
            amplitudeTrackingId='prepay-duty-alert-modal-change-button'
            theme={THEME_CONSTANTS.tertiary}
          >
            <FormattedMessage {...alertModalMessages.changeButton} />
          </Button>
          <Button onClick={handleProceed} amplitudeTrackingId='prepay-duty-alert-modal-proceed-button' theme={THEME_CONSTANTS.tertiary}>
            <FormattedMessage {...alertModalMessages.proceedButton} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

PrepayDutyAlertModal.propTypes = {
  availableDutiesOptions: AvailableDutiesOptionsProptype.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
};

export default PrepayDutyAlertModal;
